import { Item } from "../../../../pojo/Item";
import { S25Util } from "../../../../util/s25-util";

import type { CheckOccurrence, CheckResultMessageContext, Detailed, WindowMode } from "./event-quota.model";
import { TypeIds } from "./event-quota.model";
import {
    hasCountLimit,
    hasTimeLimit,
    normalizeWindowMode,
    usesDurationForWindow,
    usesFixedDatesForWindow,
} from "./event-quota.util";

/** Separates distinct quota violations in one alert modal (`innerHTML` + safeHTML). */
export const EXCEEDED_CHECK_RESULTS_MESSAGE_SEPARATOR = "<br><br>";

export function exceededPeriodPhrase(quota: {
    windowMode?: WindowMode | null;
    duration?: number | null;
    durationTypeName?: string | null;
    windowStartLabel?: string | null;
    windowEndLabel?: string | null;
}): string {
    const windowMode = normalizeWindowMode(quota.windowMode);
    if (usesFixedDatesForWindow(windowMode)) {
        const startLabel = quota.windowStartLabel?.trim();
        const endLabel = quota.windowEndLabel?.trim();
        if (startLabel && endLabel) {
            return `from ${startLabel} through ${endLabel}.`;
        }
        if (endLabel) {
            return `through ${endLabel}.`;
        }
    }
    const duration = quota.duration ?? 0;
    const durationUnit = quota.durationTypeName ?? "Days";
    return `${duration} ${durationUnit}.`;
}

export function exceededCountLimitPhrase(quota: Detailed): string {
    const windowMode = normalizeWindowMode(quota.windowMode);
    const quantity = quota.quantity ?? 0;
    if (usesDurationForWindow(windowMode)) {
        const durationTypeName = quota.durationTypeName ?? "Days";
        return `${quantity} time(s) within ${quota.duration ?? 0} ${durationTypeName}.`;
    }
    return `${quantity} time(s) ${exceededPeriodPhrase(quota)}`;
}

export function exceededTimeLimitPhrase(quota: Detailed): string {
    const totalMinutes = S25Util.ISODurationToMinutes(quota.maxTime ?? "");
    const bookedTimeLabel = S25Util.getDurationString(totalMinutes).toString();
    const windowMode = normalizeWindowMode(quota.windowMode);
    if (usesDurationForWindow(windowMode)) {
        const durationTypeName = quota.durationTypeName ?? "Days";
        return `more than ${bookedTimeLabel} within ${quota.duration ?? 0} ${durationTypeName}.`;
    }
    return `more than ${bookedTimeLabel} ${exceededPeriodPhrase(quota)}`;
}

function violatedLimitPhrases(quota: Detailed, occ: CheckOccurrence): string[] {
    const phrases: string[] = [];
    const allowedCount = S25Util.toBool(occ.allowed_count);
    const allowedTime = S25Util.toBool(occ.allowed_time);

    if (hasCountLimit(quota) && !allowedCount) {
        phrases.push(exceededCountLimitPhrase(quota));
    }
    if (hasTimeLimit(quota) && !allowedTime) {
        phrases.push(exceededTimeLimitPhrase(quota));
    }
    return phrases;
}

function joinLimitPhrases(phrases: string[]): string {
    const normalized = phrases.map((phrase) => phrase.replace(/\.\s*$/, "").trim()).filter(Boolean);
    if (!normalized.length) {
        return "";
    }
    if (normalized.length === 1) {
        return `${normalized[0]}.`;
    }
    if (normalized.length === 2) {
        return `${normalized[0]} and ${normalized[1]}.`;
    }
    return `${normalized.slice(0, -1).join(", ")}, and ${normalized[normalized.length - 1]}.`;
}

export function formatCheckResultExceededMessage(conflict: CheckResultMessageContext | undefined | null): string {
    if (!conflict?.quota || !conflict.occ) {
        return "";
    }
    return formatCheckResultExceededMessageFromPhrases(conflict, violatedLimitPhrases(conflict.quota, conflict.occ));
}

function joinStartDatePhrases(startDates: Array<Date | string | null | undefined>): string {
    const normalized = [
        ...new Set(startDates.map((startDate) => (startDate == null ? "" : String(startDate).trim())).filter(Boolean)),
    ];
    if (!normalized.length) {
        return "";
    }
    if (normalized.length === 1) {
        return normalized[0];
    }
    if (normalized.length === 2) {
        return `${normalized[0]} and ${normalized[1]}`;
    }
    return `${normalized.slice(0, -1).join(", ")}, and ${normalized[normalized.length - 1]}`;
}

function chooseAffectedDatesClause(isLocationQuota: boolean, startDateCount: number, startDatesLabel: string): string {
    if (!startDatesLabel) {
        return "";
    }
    if (isLocationQuota) {
        if (startDateCount === 1) {
            return `Please choose a different date or location for ${startDatesLabel}`;
        }
        return `Please choose different dates or locations for ${startDatesLabel}`;
    }
    if (startDateCount === 1) {
        return `Please choose a different date for ${startDatesLabel}`;
    }
    return `Please choose different dates for ${startDatesLabel}`;
}

function formatCheckResultExceededMessageFromPhrases(
    conflict: CheckResultMessageContext,
    limitPhrases: string[],
    startDates: Array<Date | string | null | undefined> = [conflict.occ?.startDt],
): string {
    if (!limitPhrases.length || !conflict.quota || !conflict.occ) {
        return "";
    }

    const limitText = joinLimitPhrases(limitPhrases);
    const quotaTypeId = S25Util.toInt(conflict.quota.type?.itemId);
    const subjectPrefix = quotaTypeId === TypeIds.Organization ? "The primary organization has" : "You have";

    const locationItem = conflict.location ?? conflict.occ.location;
    const isLocationQuota = !!conflict.occ.object_id && conflict.occ.object_type_id === Item.Ids.Location;
    const startDatesLabel = joinStartDatePhrases(startDates);
    const chooseDatesClause = chooseAffectedDatesClause(
        isLocationQuota,
        startDates.filter((startDate) => startDate != null && String(startDate).trim()).length,
        startDatesLabel,
    );

    let message: string;
    if (isLocationQuota) {
        const locationName = locationItem?.itemName ?? "this location";
        message =
            `${subjectPrefix} exceeded the maximum usage of ${locationName} by booking it ${limitText} ` +
            chooseDatesClause;
    } else {
        message =
            `${subjectPrefix} exceeded the maximum number of reservations by booking ${limitText} ` + chooseDatesClause;
    }

    if (conflict.quota.comment) {
        message = message.concat(" ", conflict.quota.comment);
    }
    return message;
}

function checkResultGroupKey(conflict: CheckResultMessageContext): string {
    return [
        conflict.occ?.uuid ?? "",
        conflict.occ?.startDt ?? "",
        conflict.occ?.object_id ?? "",
        conflict.quota?.type?.itemId ?? "",
        conflict.quota?.itemId ?? "",
    ].join("|");
}

function exceededMessageGroupKey(conflict: CheckResultMessageContext, limitPhrases: string[]): string {
    const quota = conflict.quota;
    const occ = conflict.occ;
    const quotaIdentity = [
        quota?.itemId ?? "",
        quota?.quantity ?? "",
        quota?.maxTime ?? "",
        quota?.duration ?? "",
        normalizeWindowMode(quota?.windowMode),
        quota?.windowStartLabel ?? "",
        quota?.windowEndLabel ?? "",
    ].join(":");
    const locationItem = conflict.location ?? occ?.location;
    return [
        quota?.type?.itemId ?? "",
        quotaIdentity,
        joinLimitPhrases(limitPhrases),
        occ?.object_id ?? "",
        occ?.object_type_id ?? "",
        locationItem?.itemId ?? "",
        locationItem?.itemName ?? "",
    ].join("|");
}

export function formatCheckResultsExceededMessage(
    quotas: CheckResultMessageContext | CheckResultMessageContext[] | undefined | null,
    quotaTypeIds?: Array<number | string>,
): string {
    let conflicts = S25Util.array.forceArray(quotas) as CheckResultMessageContext[];
    const typeFilter = S25Util.array.forceArray(quotaTypeIds).map((typeId) => S25Util.toInt(typeId));
    if (typeFilter.length) {
        conflicts = conflicts.filter(
            (conflict) => typeFilter.indexOf(S25Util.toInt(conflict.quota?.type?.itemId)) > -1,
        );
    }
    if (!conflicts.length) {
        return "";
    }

    const grouped = new Map<string, CheckResultMessageContext[]>();
    for (const conflict of conflicts) {
        const key = checkResultGroupKey(conflict);
        const group = grouped.get(key) ?? [];
        group.push(conflict);
        grouped.set(key, group);
    }

    type OccurrenceExceededGroup = {
        primaryConflict: CheckResultMessageContext;
        mergedPhrases: string[];
        startDates: Array<Date | string>;
    };

    const byOccurrence: OccurrenceExceededGroup[] = [];
    for (const group of grouped.values()) {
        const primary = group[0];
        const mergedPhrases: string[] = [];
        for (const conflict of group) {
            if (!conflict.quota || !conflict.occ) {
                continue;
            }
            for (const phrase of violatedLimitPhrases(conflict.quota, conflict.occ)) {
                if (mergedPhrases.indexOf(phrase) === -1) {
                    mergedPhrases.push(phrase);
                }
            }
        }
        if (!mergedPhrases.length || !primary.occ) {
            continue;
        }
        const comments: string[] = [];
        for (const conflict of group) {
            const comment = conflict.quota?.comment?.trim();
            if (comment && comments.indexOf(comment) === -1) {
                comments.push(comment);
            }
        }
        const primaryConflict: CheckResultMessageContext = {
            ...primary,
            quota: primary.quota
                ? { ...primary.quota, comment: comments.join(" ") || primary.quota.comment }
                : primary.quota,
            occ: primary.occ,
            location: primary.location ?? primary.occ?.location,
        };
        byOccurrence.push({
            primaryConflict,
            mergedPhrases,
            startDates: [primary.occ.startDt],
        });
    }

    const byMessage = new Map<string, OccurrenceExceededGroup[]>();
    for (const occurrenceGroup of byOccurrence) {
        const key = exceededMessageGroupKey(occurrenceGroup.primaryConflict, occurrenceGroup.mergedPhrases);
        const cluster = byMessage.get(key) ?? [];
        cluster.push(occurrenceGroup);
        byMessage.set(key, cluster);
    }

    const messages: string[] = [];
    for (const cluster of byMessage.values()) {
        const lead = cluster[0];
        const startDates = cluster.flatMap((item) => item.startDates);
        const comments: string[] = [];
        for (const item of cluster) {
            const comment = item.primaryConflict.quota?.comment?.trim();
            if (comment && comments.indexOf(comment) === -1) {
                comments.push(comment);
            }
        }
        const primaryConflict: CheckResultMessageContext = {
            ...lead.primaryConflict,
            quota: lead.primaryConflict.quota
                ? {
                      ...lead.primaryConflict.quota,
                      comment: comments.join(" ") || lead.primaryConflict.quota.comment,
                  }
                : lead.primaryConflict.quota,
        };
        messages.push(formatCheckResultExceededMessageFromPhrases(primaryConflict, lead.mergedPhrases, startDates));
    }

    return messages.join(EXCEEDED_CHECK_RESULTS_MESSAGE_SEPARATOR);
}
